import { RadarBriefingBanner } from './RadarBriefingBanner';
import { RadarWorkThreadCard } from './RadarWorkThreadCard';
import { buildAttachRelation, RadarUngroupedItemsSection } from './RadarUngroupedItemsSection';
import { AppErrorBoundary } from '../../ui/common/AppErrorBoundary';
import { RadarToolsGrid } from './RadarToolsGrid';
import { RadarAiAssistantPanel } from './RadarAiAssistantPanel';
import { FilterButton } from '../../ui/common/FilterButton';
import {
  RightWorkspaceSidebarSlot,
  useRightWorkspaceSidebar,
} from '../layout/RightWorkspaceSidebar';
import { Sparkles } from 'lucide-react';
import { AttentionItem, ViewMode } from '../../domain/types';
import {
  buildRadarBriefing,
  radarActionableItems,
  buildWorkThreads,
  filterThreadsByBriefingBucket,
  partitionWorkThreads,
  sortWorkThreads,
  RADAR_AXES,
  type BriefingBucketFilter,
  type BriefingBucketId,
  type RadarThreadSortMode,
  type WorkThread,
  type RadarBriefingHighlight,
} from '../../domain/radarThreads';
import {
  defaultAxisFilterState,
  filterByAxes,
  type AxisFilterState,
} from '../../domain/multiAxisFiltering';
import { resourceIdForAttentionItem } from '../../domain/workContextBridge';
import { useZunelo } from '../../context/ZuneloContext';
import React, { useCallback, useMemo, useRef, useState } from 'react';

interface RadarViewProps {
  onOpenTool?: (tool: 'daily' | 'notes' | 'wiki') => void;
}

function revealContextosSection(el: HTMLElement | null): void {
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  el.focus({ preventScroll: true });
}

function ThreadSection({
  title,
  testId,
  threads,
  expandedThreadId,
  setExpandedThreadId,
  onAttach,
  attachThreadId,
  onPassToAssistant,
  aiAttachmentIds,
  onSnooze,
}: {
  title: string;
  testId: string;
  threads: WorkThread[];
  expandedThreadId: string | null;
  setExpandedThreadId: React.Dispatch<React.SetStateAction<string | null>>;
  onAttach?: (thread: WorkThread) => void;
  attachThreadId?: string | null;
  onPassToAssistant?: (thread: WorkThread) => void;
  aiAttachmentIds?: Set<string>;
  onSnooze: (thread: WorkThread, durationMs: number) => void;
}) {
  const handleToggle = useCallback(
    (thread: WorkThread) => {
      setExpandedThreadId((currentId) => (currentId === thread.id ? null : thread.id));
    },
    [setExpandedThreadId],
  );
  const handleAttach = useCallback((thread: WorkThread) => onAttach?.(thread), [onAttach]);
  const handleSnooze = useCallback(
    (thread: WorkThread, durationMs: number) => onSnooze(thread, durationMs),
    [onSnooze],
  );
  if (threads.length === 0) return null;
  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {title ? (
        <h3
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            color: 'var(--z-dim)',
          }}
        >
          {title}
        </h3>
      ) : null}
      {threads.map((thread) => (
        <AppErrorBoundary key={thread.id} variant="item">
          <RadarWorkThreadCard
            thread={thread}
            isExpanded={expandedThreadId === thread.id}
            onToggle={handleToggle}
            onRequestAttach={onAttach ? handleAttach : undefined}
            isAttachOpen={attachThreadId === thread.id}
            onPassToAssistant={onPassToAssistant}
            isPassedToAssistant={aiAttachmentIds?.has(thread.id) ?? false}
            onSnooze={handleSnooze}
          />
        </AppErrorBoundary>
      ))}
    </div>
  );
}

export const RadarView: React.FC<RadarViewProps> = ({ onOpenTool }) => {
  const {
    items,
    agents,
    reminders,
    notes,
    workContextGraph,
    createManualWorkHub,
    attachResourceToHub,
    upsertWorkContextRelation,
    navigateToItem,
    setSearchTarget,
    refreshAll,
    aiAttachments,
    sendToAssistant,
    removeAiAttachment,
    focusedContextId,
    clearFocusedContext,
  } = useZunelo();

  const rightSidebar = useRightWorkspaceSidebar();

  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<RadarThreadSortMode>('priority');
  const [filters, setFilters] = useState<AxisFilterState>(() => defaultAxisFilterState(RADAR_AXES));
  const [newHubTitle, setNewHubTitle] = useState('');
  const [isCreatingHub, setIsCreatingHub] = useState(false);
  const [attachThreadId, setAttachThreadId] = useState<string | null>(null);
  const [snoozedUntil, setSnoozedUntil] = useState<Record<string, number>>({});

  const [syncedAtMs] = useState(() => Date.now());
  const [briefingBucket, setBriefingBucket] = useState<BriefingBucketFilter>('all');
  const [showAllHighlights, setShowAllHighlights] = useState(false);
  const [assistantPickerRequest, setAssistantPickerRequest] = useState(0);
  const contextosRef = useRef<HTMLElement>(null);
  const actionableItems = useMemo(() => radarActionableItems(items), [items]);

  const briefing = useMemo(
    () => buildRadarBriefing(actionableItems, agents, reminders, { syncedAtMs }),
    [actionableItems, agents, reminders, syncedAtMs],
  );

  const workThreads = useMemo(() => {
    const raw = buildWorkThreads(actionableItems, agents, {
      extraRelations: workContextGraph.relations,
      manualHubs: workContextGraph.manualHubs,
      notes,
      reminders,
    });
    return sortWorkThreads(raw, sortBy);
  }, [actionableItems, agents, notes, reminders, workContextGraph, sortBy]);

  const visibleWorkThreads = useMemo(() => {
    const now = Date.now();
    return workThreads.filter((thread) => (snoozedUntil[thread.id] ?? 0) <= now);
  }, [snoozedUntil, workThreads]);

  const { contexts: allContexts, withoutContext: allUngrouped } = useMemo(
    () => partitionWorkThreads(visibleWorkThreads),
    [visibleWorkThreads],
  );
  const contexts = useMemo(
    () =>
      filterThreadsByBriefingBucket(filterByAxes(allContexts, RADAR_AXES, filters), briefingBucket),
    [allContexts, filters, briefingBucket],
  );
  const withoutContext = useMemo(
    () => filterThreadsByBriefingBucket(allUngrouped, briefingBucket),
    [allUngrouped, briefingBucket],
  );
  const skipAutoExpand = React.useRef(false);

  const setExpandedThreadIdSafe = React.useCallback((id: React.SetStateAction<string | null>) => {
    skipAutoExpand.current = true;
    setExpandedThreadId(id);
  }, []);

  React.useEffect(() => {
    if (skipAutoExpand.current) return;
    const preferred = contexts.find((t) => t.id === 'DEV-3056') || contexts[0] || withoutContext[0];
    if (!preferred) return;
    setExpandedThreadId((current) => (current === preferred.id ? current : preferred.id));
  }, [contexts, withoutContext]);

  // A "Ver contexto" chip on a Jira/Slack/GitHub card requested this exact
  // WorkThread — expand it and scroll it into view, then consume the
  // request. See openspec/changes/provider-item-interaction-ux (Work Context
  // chips must focus the exact context, not just Radar root).
  React.useEffect(() => {
    if (!focusedContextId) return;
    const target = [...contexts, ...withoutContext].find((t) => t.id === focusedContextId);
    if (!target) return;
    skipAutoExpand.current = true;
    setExpandedThreadId(target.id);
    const raf = window.requestAnimationFrame(() => {
      const testId = `radar-thread-expanded-${target.id}`;
      const selector =
        typeof CSS !== 'undefined' && CSS.escape
          ? `[data-testid="${CSS.escape(testId)}"]`
          : `[data-testid="${testId}"]`;
      document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    clearFocusedContext();
    return () => window.cancelAnimationFrame(raf);
  }, [focusedContextId, contexts, withoutContext, clearFocusedContext]);

  React.useEffect(() => {
    if (!attachThreadId) return;
    const stillVisible =
      contexts.some((thread) => thread.id === attachThreadId) ||
      withoutContext.some((thread) => thread.id === attachThreadId);
    if (!stillVisible) setAttachThreadId(null);
  }, [attachThreadId, contexts, withoutContext]);

  const handleSnooze = (thread: WorkThread, durationMs: number) => {
    const until = Date.now() + durationMs;
    setSnoozedUntil((current) => ({ ...current, [thread.id]: until }));
    setExpandedThreadIdSafe(null);
    window.setTimeout(() => {
      setSnoozedUntil((current) => {
        if (current[thread.id] !== until) return current;
        const next = { ...current };
        delete next[thread.id];
        return next;
      });
    }, durationMs);
  };

  const handleNavigate = (targetView: ViewMode, targetId: string) => {
    navigateToItem(targetView, targetId);
  };

  const handleSelectBriefingBucket = (id: BriefingBucketId) => {
    setBriefingBucket((current) => (current === id ? 'all' : id));
    revealContextosSection(contextosRef.current);
  };

  const handleViewFullBriefing = () => {
    setBriefingBucket('all');
    setShowAllHighlights(true);
    revealContextosSection(contextosRef.current);
  };

  const handleAddHighlightToContext = (highlight: RadarBriefingHighlight) => {
    const thread = workThreads.find(
      (candidate) =>
        candidate.id === highlight.id ||
        candidate.actions.some((action) => action.targetId === highlight.id),
    );
    if (thread) sendToAssistant(thread);
    setAssistantPickerRequest((request) => request + 1);
  };

  const handleOpenTool = (tool: 'daily' | 'notes' | 'wiki') => {
    if (onOpenTool) {
      onOpenTool(tool);
    }
  };

  const handleCreateHub = async () => {
    if (!newHubTitle.trim() || isCreatingHub) return;
    setIsCreatingHub(true);
    try {
      const hub = await createManualWorkHub(newHubTitle);
      if (hub) {
        setNewHubTitle('');
        setExpandedThreadIdSafe(hub.id);
      }
    } finally {
      setIsCreatingHub(false);
    }
  };

  const handleCreateContextFromThread = async (
    thread: WorkThread,
    item: AttentionItem | undefined,
  ) => {
    const hub = await createManualWorkHub(thread.title || thread.key);
    if (!hub) return;
    if (item) {
      await attachResourceToHub(resourceIdForAttentionItem(item), hub.id);
    }
    setExpandedThreadIdSafe(hub.id);
  };

  const handleAttachItemToContext = async (
    item: AttentionItem,
    target: WorkThread,
  ): Promise<boolean> => {
    const plan = buildAttachRelation(item, target);
    if (plan.kind === 'hub') {
      return attachResourceToHub(resourceIdForAttentionItem(item), plan.hubId);
    }
    return upsertWorkContextRelation(plan.relation);
  };

  const attachTarget = workThreads.find((t) => t.id === attachThreadId);

  return (
    <div data-testid="radar-view-container" className="z-radar-container">
      {/* Center Scrollable Stream */}
      <div className="z-radar-stream">
        {/* Content Wrapper */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
        >
          {/* Header Section (Desktop only - mobile uses MainWorkspaceHeader) */}
          <div
            className="z-radar-desktop-header"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: 24,
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  color: 'var(--z-text)',
                }}
              >
                Radar de Atención
              </h1>
              <span style={{ fontSize: 13, color: 'var(--z-dim)' }}>
                Tu atención, enfocada en lo que mueve al equipo.
              </span>
            </div>

            {/* Right Header Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 14px',
                  borderRadius: 999,
                  background: 'rgba(16, 185, 129, 0.12)',
                  color: '#10B981',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: '#10B981',
                    animation: 'zpulse 2s infinite ease-in-out',
                  }}
                />
                Sincronizado
              </div>

              <button
                onClick={() => setSearchTarget('global')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 14px',
                  borderRadius: 999,
                  background: 'var(--z-surface)',
                  border: '1px solid var(--z-border)',
                  color: 'var(--z-dim)',
                  fontSize: 12,
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <span>Buscar...</span>
                <kbd
                  style={{
                    fontSize: 10,
                    opacity: 0.6,
                    background: 'var(--z-fill)',
                    padding: '1px 5px',
                    borderRadius: 4,
                  }}
                >
                  ⌘K
                </kbd>
              </button>
            </div>
          </div>

          {/* 1. Briefing de hoy */}
          <RadarBriefingBanner
            briefing={briefing}
            selectedBucket={briefingBucket}
            showAllHighlights={showAllHighlights}
            onSelectBucket={handleSelectBriefingBucket}
            onOpenHighlight={handleNavigate}
            onAddHighlightToContext={handleAddHighlightToContext}
            onViewFullBriefing={handleViewFullBriefing}
          />

          {/* 2. Contextos activos */}
          <section
            id="radar-contextos"
            data-testid="radar-contextos"
            ref={contextosRef}
            tabIndex={-1}
            style={{ display: 'flex', flexDirection: 'column', gap: 12, outline: 'none' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 15,
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: 'var(--z-text)',
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                }}
              >
                Contextos activos
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--z-dim)' }}>
                  {contexts.length} contexto{contexts.length === 1 ? '' : 's'}
                </span>
              </h2>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 12,
                  color: 'var(--z-dim)',
                }}
              >
                <span>Ordenar por:</span>
                <select
                  data-testid="radar-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as RadarThreadSortMode)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--z-text)',
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                >
                  <option value="priority">Prioridad</option>
                  <option value="unread">No vistas</option>
                  <option value="recency">Más recientes</option>
                </select>
                <FilterButton
                  axes={RADAR_AXES}
                  items={allContexts}
                  value={filters}
                  onChange={setFilters}
                />
              </div>
            </div>

            <form
              data-testid="radar-new-hub-form"
              onSubmit={(e) => {
                e.preventDefault();
                void handleCreateHub();
              }}
              style={{ display: 'flex', gap: 8, alignItems: 'center' }}
            >
              <input
                data-testid="radar-new-hub-input"
                value={newHubTitle}
                onChange={(e) => setNewHubTitle(e.target.value)}
                placeholder="Nuevo contexto (sin Jira)…"
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '8px 12px',
                  borderRadius: 10,
                  border: '1px solid var(--z-border)',
                  background: 'var(--z-surface)',
                  color: 'var(--z-text)',
                  fontSize: 13,
                }}
              />
              <button
                type="submit"
                data-testid="radar-new-hub-submit"
                disabled={!newHubTitle.trim() || isCreatingHub}
                style={{
                  padding: '8px 14px',
                  borderRadius: 10,
                  border: 'none',
                  background: 'var(--z-accent)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: newHubTitle.trim() ? 'pointer' : 'not-allowed',
                  opacity: newHubTitle.trim() ? 1 : 0.5,
                }}
              >
                Nuevo contexto
              </button>
            </form>

            <ThreadSection
              title=""
              testId="radar-section-contexts"
              threads={contexts}
              expandedThreadId={expandedThreadId}
              setExpandedThreadId={setExpandedThreadIdSafe}
              onAttach={(thread) =>
                setAttachThreadId(attachThreadId === thread.id ? null : thread.id)
              }
              attachThreadId={attachThreadId}
              onPassToAssistant={sendToAssistant}
              aiAttachmentIds={new Set(aiAttachments.map((att) => att.id))}
              onSnooze={handleSnooze}
            />

            {attachTarget?.isManual && (
              <div
                data-testid="radar-attach-picker"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  padding: 12,
                  borderRadius: 12,
                  border: '1px solid var(--z-border)',
                  background: 'var(--z-surface)',
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--z-dim)' }}>
                  Añadir a «{attachTarget.title}»
                </span>
                {items.slice(0, 12).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`radar-attach-item-${item.id}`}
                    onClick={() => {
                      void attachResourceToHub(
                        resourceIdForAttentionItem(item),
                        attachTarget.id,
                      ).then((ok) => {
                        if (ok) setAttachThreadId(null);
                      });
                    }}
                    style={{
                      textAlign: 'left',
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid var(--z-border)',
                      background: 'var(--z-fill)',
                      color: 'var(--z-text)',
                      cursor: 'pointer',
                      fontSize: 12,
                    }}
                  >
                    <strong>{item.primary || item.id}</strong> · {item.title}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setAttachThreadId(null)}
                  style={{
                    marginTop: 4,
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--z-dim)',
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  Cancelar
                </button>
              </div>
            )}

            <RadarUngroupedItemsSection
              threads={withoutContext}
              contexts={allContexts}
              items={items}
              onRefresh={() => {
                void refreshAll();
              }}
              onCreateContext={handleCreateContextFromThread}
              onAttachToContext={handleAttachItemToContext}
              onNavigate={(thread) => {
                const action = thread.actions[0];
                if (action) handleNavigate(action.targetView, action.targetId);
                else setExpandedThreadIdSafe(thread.id);
              }}
            />
          </section>

          {/* 3. Herramientas Grid */}
          <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: 'var(--z-text)',
              }}
            >
              Herramientas
            </h2>

            <RadarToolsGrid reminders={reminders} notes={notes} onOpenTool={handleOpenTool} />
          </section>
        </div>
      </div>

      <RightWorkspaceSidebarSlot
        id="radar-assistant"
        accessibleLabel="Asistente IA del Radar"
        title="Asistente IA"
        description="Listo para ayudar"
        icon={<Sparkles size={17} strokeWidth={1.8} />}
        body={
          <RadarAiAssistantPanel
            activeThread={workThreads.find((t) => t.id === expandedThreadId) || workThreads[0]}
            attachments={aiAttachments}
            attachableThreads={workThreads}
            onNavigate={handleNavigate}
            onAddAttachment={sendToAssistant}
            onRemoveAttachment={removeAiAttachment}
            openPickerRequest={assistantPickerRequest}
            onRestore={rightSidebar.restore}
          />
        }
      />
    </div>
  );
};
